/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { rgba } from 'polished';

export const RESOURCE_INFO_MIN_HEIGHT = 24;

const RESOURCE_INFO_FULL_HEIGHT = 56;

const ResourceInfoWrapper = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  max-height: ${({ $assetHeight }) => `${$assetHeight}px`};
  box-sizing: border-box;
  padding: 4px 8px;
  background-color: ${({ theme }) => rgba(theme.colors.bg.primary, 0.6)};
  text-overflow: ellipsis;
  overflow: hidden;
  text-decoration: none;
  z-index: 2;
  pointer-events: none;
`;

const Title = styled.p`
  display: -webkit-box;
  font-size: 12px;
  font-weight: 700;
  line-height: 0.95rem;
  margin: 0;
  overflow: hidden;
  -webkit-line-clamp: ${({ $isCompact }) => ($isCompact ? 1 : 2)};
  -webkit-box-orient: vertical;
`;

const PublishDate = styled.p`
  color: rgba(145, 152, 153, 1);
  font-size: 12px;
  font-weight: 400;
  line-height: 1rem;
  margin: 0;
`;

const ResourceInfo = ({ title, date, height }) => {
  if (height < RESOURCE_INFO_MIN_HEIGHT) {
    return null;
  }

  const isCompact = height < RESOURCE_INFO_FULL_HEIGHT;
  const formatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const formattedDate = date ? formatter.format(new Date(date)) : null;

  return (
    <ResourceInfoWrapper $assetHeight={height}>
      <Title $isCompact={isCompact}>{title}</Title>
      {!isCompact && formattedDate && (
        <PublishDate>{formattedDate}</PublishDate>
      )}
    </ResourceInfoWrapper>
  );
};

ResourceInfo.propTypes = {
  title: PropTypes.string.isRequired,
  date: PropTypes.string,
  height: PropTypes.number.isRequired,
};

export default ResourceInfo;
